import { z } from 'zod'

import { PAYMENT_METHODS } from '#admin/features/billing/payments-api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * Payment methods. Platform staff keep the catalog; an edition turns methods on here, and the
 * operator selects one of them when a registration is submitted.
 */
const selectableMethodSchema = z.object({
  code: z.string(),
  name: z.string(),
  kind: z.enum(PAYMENT_METHODS),
  /** Null for a manual method. Otherwise the system that opens the payment online. */
  provider: z.string().nullable(),
})

const adminMethodSchema = selectableMethodSchema.extend({
  enabled: z.boolean(),
  retired: z.boolean(),
})

export type SelectableMethod = z.infer<typeof selectableMethodSchema>
export type AdminMethod = z.infer<typeof adminMethodSchema>

/** What a payment under the method is recorded as, in words. */
export const KIND_LABEL: Record<AdminMethod['kind'], string> = {
  virtual_account: 'Virtual account',
  bank_transfer: 'Bank transfer',
  card: 'Card',
  cash: 'Cash',
  waiver: 'Waiver',
  other: 'Other',
}

export function fetchAdminPaymentMethods(signal?: AbortSignal) {
  return api.get('/v1/billing/admin/payment-methods', {
    schema: z.object({ items: z.array(adminMethodSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function setPaymentMethodEnabled({ code, enabled }: { code: string; enabled: boolean }) {
  return api.patch(`/v1/billing/admin/payment-methods/${code}`, {
    schema: adminMethodSchema,
    body: { enabled },
  })
}

/** The methods an operator or a delegate can select. Needs `billing.read` only. */
export function fetchSelectablePaymentMethods(signal?: AbortSignal) {
  return api.get('/v1/billing/payment-methods', {
    schema: z.object({ items: z.array(selectableMethodSchema) }),
    ...(signal ? { signal } : {}),
  })
}
